<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useBountyExtensionStore } from '@/stores/bounty-extension'
import { formatDate } from '@/utils/format'
import {
  defaultDate,
  expiryForDate,
  formatDateKey,
  isSelectableDate,
  parseDateKey
} from '@/utils/bounty-extension'

const props = defineProps<{
  modelValue: boolean
  bountyId: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  extended: []
}>()

const store = useBountyExtensionStore()
const { offerWindow, loading, submitting, error } = storeToRefs(store)

const selectedKey = ref<string | null>(null)
const notifyPoster = ref(true)

watch(
  () => [props.modelValue, props.bountyId] as const,
  ([open, id]) => {
    selectedKey.value = null
    notifyPoster.value = true
    if (open && id) void store.open(id)
    if (!open) store.reset()
  },
  { immediate: true }
)

const current = computed(() =>
  offerWindow.value?.currentExpiresAt ? new Date(offerWindow.value.currentExpiresAt) : null
)
const latest = computed(() =>
  offerWindow.value?.latestExpiresAt ? new Date(offerWindow.value.latestExpiresAt) : null
)
const canExtend = computed(
  () => !!offerWindow.value?.canExtend && !!current.value && !!latest.value
)
const isExpired = computed(() => !!current.value && current.value <= new Date())

// Start the picker on a sensible day once the window is known.
watch(offerWindow, (value) => {
  if (!value?.canExtend || !current.value || !latest.value) return
  selectedKey.value = formatDateKey(defaultDate(current.value, new Date(), latest.value))
})

function isAllowed(key: string): boolean {
  if (!current.value || !latest.value) return false
  return isSelectableDate(parseDateKey(key), current.value, latest.value, new Date())
}

const selectedDate = computed(() => (selectedKey.value ? parseDateKey(selectedKey.value) : null))
const canSubmit = computed(
  () => canExtend.value && !!selectedKey.value && isAllowed(selectedKey.value) && !submitting.value
)

const close = () => emit('update:modelValue', false)

async function submit() {
  if (!props.bountyId || !selectedDate.value || !latest.value || !canSubmit.value) return
  const newExpiresAt = expiryForDate(selectedDate.value, latest.value)
  const ok = await store.extend(props.bountyId, newExpiresAt, notifyPoster.value)
  if (!ok) return
  emit('extended')
  close()
}
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)">
    <q-card style="min-width: 320px; max-width: 420px; width: 100%">
      <q-card-section class="row items-center no-wrap q-pb-none">
        <div class="text-h6 col">Extend offer</div>
        <q-btn flat round dense icon="mdi-close" aria-label="Close" @click="close" />
      </q-card-section>

      <q-card-section v-if="error" class="q-pb-none">
        <q-banner rounded class="bg-negative text-white" data-testid="extend-error">
          {{ error }}
        </q-banner>
      </q-card-section>

      <q-card-section v-if="loading" class="flex flex-center q-pa-md">
        <q-spinner />
      </q-card-section>

      <q-card-section v-else-if="offerWindow && !canExtend" data-testid="extend-blocked">
        {{ offerWindow.blockedReason }}
      </q-card-section>

      <q-card-section v-else-if="offerWindow && current && latest">
        <div class="q-mb-sm">
          Current expiry: {{ formatDate(current) }}
          <span v-if="isExpired">(expired)</span>
        </div>
        <div class="text-subtitle2 q-mb-xs">New expiry</div>
        <q-date
          v-model="selectedKey"
          minimal
          flat
          bordered
          mask="YYYY/MM/DD"
          :options="isAllowed"
          class="full-width"
        />
        <div class="q-mt-sm" data-testid="extend-selected">
          Selected: {{ selectedDate ? formatDate(selectedDate) : 'none' }}
        </div>
        <div class="text-caption text-grey-7" data-testid="extend-max">
          Max: {{ formatDate(latest) }}
        </div>
        <q-checkbox v-model="notifyPoster" label="Notify the poster" class="q-mt-sm" />
      </q-card-section>

      <q-separator />

      <q-card-actions align="right">
        <q-btn v-if="offerWindow && !canExtend" flat label="Close" @click="close" />
        <template v-else>
          <q-btn flat label="Cancel" @click="close" />
          <q-btn
            color="primary"
            label="Extend"
            :disable="!canSubmit"
            :loading="submitting"
            @click="submit"
          />
        </template>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
